import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, Info } from 'lucide-react';
import { apiService } from '../../services/apiService';

interface AIModel {
  id: string;
  providerModelId: string;
  name: string;
  provider: string;
  description?: string;
  price_per_token?: string;
  capabilities?: Record<string, number>;
  requires_premium?: boolean;
  contextWindow?: number;
  maxTokens?: number;
  supports_vision?: boolean;
  supports_streaming?: boolean;
  taskDeterminationModel?: boolean;
  category?: string;
  ranked_position?: number;
  date_of_model?: string;
  isUncensored?: boolean;
  isPrivacyCompliant?: boolean;
  isCustomEndpoint?: boolean;
  customEndpointUrl?: string;
  customApiKey?: string;
  customApiKeyEncrypted?: string;
  customHeaders?: Record<string, string>;
  [key: string]: any;
}

interface ModelEditModalProps {
  model: AIModel | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (model: AIModel) => Promise<string | null>;
  isAdmin: boolean;
}

type TabType = 'basic' | 'capabilities' | 'endpoint';

export default function ModelEditModal({ model, isOpen, onClose, onSave, isAdmin }: ModelEditModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('basic');
  const [editedModel, setEditedModel] = useState<AIModel | null>(null);
  const [error, setError] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [connectionError, setConnectionError] = useState<string>('');

  useEffect(() => {
    if (isOpen && model) {
      const processedModel = {
        ...model,
        capabilities: typeof model.capabilities === 'string'
          ? JSON.parse(model.capabilities)
          : model.capabilities || {},
        customHeaders: typeof model.customHeaders === 'string'
          ? JSON.parse(model.customHeaders)
          : model.customHeaders || {}
      };
      setEditedModel(processedModel);
      setActiveTab('basic');
      setError('');
    }
  }, [isOpen, model]);

  const handleFieldChange = (field: string, value: any) => {
    if (!editedModel) return;

    if (field === 'providerModelId') {
      setEditedModel({ ...editedModel, id: value, providerModelId: value });
      return;
    }

    setEditedModel({ ...editedModel, [field]: value });
  };

  const handleCapabilityChange = (capability: string, value: number) => {
    if (!editedModel) return;

    setEditedModel({
      ...editedModel,
      capabilities: {
        ...editedModel.capabilities,
        [capability]: value
      }
    });
  };

  const handleTestConnection = async () => {
    if (!editedModel || !editedModel.customEndpointUrl || !editedModel.customApiKey) {
      setConnectionError('Please provide both endpoint URL and API key');
      return;
    }

    setTestingConnection(true);
    setConnectionStatus('idle');
    setConnectionError('');

    try {
      await apiService.post('/portal/models/test-connection', {
        url: editedModel.customEndpointUrl,
        apiKey: editedModel.customApiKey,
        headers: editedModel.customHeaders,
        modelId: editedModel.providerModelId || 'test'
      });

      setConnectionStatus('success');
    } catch (err: any) {
      setConnectionStatus('error');
      const errorMessage = err.response?.data?.error || err.message || 'Connection failed';
      setConnectionError(errorMessage);
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSave = async () => {
    if (!editedModel) return;

    if (!editedModel.name || !editedModel.providerModelId) {
      setError('Name and Provider Model ID are required');
      return;
    }

    if (editedModel.provider === 'custom' && (!editedModel.customEndpointUrl || !editedModel.customApiKey)) {
      setError('Custom endpoint URL and API key are required for custom providers');
      return;
    }

    setSaving(true);
    setError('');

    const errorMessage = await onSave(editedModel);

    setSaving(false);

    if (!errorMessage) {
      onClose();
    } else {
      setError(errorMessage);
    }
  };

  if (!isOpen || !editedModel) return null;

  const tabs = editedModel.provider === 'custom'
    ? ['basic', 'capabilities', 'endpoint']
    : ['basic', 'capabilities'];

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] !m-0">
      <div className="bg-white rounded-lg w-full max-w-4xl h-[95vh] overflow-hidden flex flex-col relative z-[10000]">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Edit Model</h2>
            <p className="text-sm text-gray-500 mt-1">{editedModel.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <div className="flex px-6">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as TabType)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors capitalize ${
                  activeTab === tab
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab === 'endpoint' ? 'Custom Endpoint' : tab}
              </button>
            ))}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded flex items-start gap-2">
            <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          {activeTab === 'basic' && (
            <BasicTab model={editedModel} onChange={handleFieldChange} isAdmin={isAdmin} />
          )}

          {activeTab === 'capabilities' && (
            <CapabilitiesTab
              capabilities={editedModel.capabilities || {}}
              onChange={handleCapabilityChange}
            />
          )}

          {activeTab === 'endpoint' && editedModel.provider === 'custom' && (
            <EndpointTab
              model={editedModel}
              onChange={handleFieldChange}
              onTestConnection={handleTestConnection}
              testingConnection={testingConnection}
              connectionStatus={connectionStatus}
              error={connectionError}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end items-center gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Save size={16} />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Tab Components
function BasicTab({ model, onChange, isAdmin }: any) {
  const [showTooltip, setShowTooltip] = useState<string | null>(null);

  const tooltips: Record<string, string> = {
    name: 'A friendly display name for this model',
    providerModelId: 'Unique identifier used by the provider (e.g., gpt-4, claude-3-sonnet)',
    contextWindow: 'Maximum number of tokens the model can process in input',
    maxTokens: 'Maximum number of tokens the model can generate in output',
    category: 'Model category for filtering and organization',
    ranked_position: 'Global ranking position (1 = best)',
    price_per_token: 'Pricing format: "$1.25/1M in, $10/1M out" or "free". System averages input and output costs for price priority sorting.',
    vision: 'Model can process and understand images',
    streaming: 'Model supports real-time streaming responses',
    privacy: 'Model is privacy-compliant for sensitive data',
    uncensored: 'Model handles sensitive content without restrictions',
    taskDet: 'Use this model for task determination decisions',
    premium: 'Model requires premium subscription',
  };

  const InfoIcon = ({ field, position = "below" }: { field: string; position?: "above" | "below" }) => (
    <div className="relative inline-block ml-1">
      <Info
        size={14}
        className="text-gray-400 hover:text-gray-600 cursor-help inline-block"
        onMouseEnter={() => setShowTooltip(field)}
        onMouseLeave={() => setShowTooltip(null)}
      />
      {showTooltip === field && (
        <div className={`absolute ${position === "above" ? "right-0 bottom-6" : "left-0 top-6"} z-[60] w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg`}>
          {tooltips[field]}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Model Name *
              <InfoIcon field="name" />
            </label>
            <input
              type="text"
              value={model.name}
              onChange={(e) => onChange('name', e.target.value)}
              placeholder="GPT-4 Turbo"
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Provider Model ID *
              <InfoIcon field="providerModelId" />
            </label>
            <input
              type="text"
              value={model.providerModelId}
              onChange={(e) => onChange('providerModelId', e.target.value)}
              placeholder="gpt-4-turbo"
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
                <InfoIcon field="category" />
              </label>
              <select
                value={model.category || ''}
                onChange={(e) => onChange('category', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select...</option>
                <option value="general">General</option>
                <option value="reasoning">Reasoning</option>
                <option value="coding">Coding</option>
                <option value="creative">Creative</option>
                <option value="vision">Vision</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rank Position
                <InfoIcon field="ranked_position" />
              </label>
              <input
                type="number"
                value={model.ranked_position ?? ''}
                onChange={(e) => onChange('ranked_position', e.target.value === '' ? undefined : parseInt(e.target.value))}
                placeholder="999"
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Context Window
                <InfoIcon field="contextWindow" />
              </label>
              <input
                type="number"
                value={model.contextWindow || ''}
                onChange={(e) => onChange('contextWindow', parseInt(e.target.value))}
                placeholder="8192"
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Tokens
                <InfoIcon field="maxTokens" />
              </label>
              <input
                type="number"
                value={model.maxTokens || ''}
                onChange={(e) => onChange('maxTokens', parseInt(e.target.value))}
                placeholder="4096"
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price/Token
                <InfoIcon field="price_per_token" />
              </label>
              <input
                type="text"
                value={model.price_per_token || ''}
                onChange={(e) => onChange('price_per_token', e.target.value)}
                placeholder="$1.25/1M in, $10/1M out"
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Provider
            </label>
            <select
              value={model.provider}
              onChange={(e) => onChange('provider', e.target.value)}
              disabled={!isAdmin}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            >
              {isAdmin && <option value="openrouter">OpenRouter</option>}
              {isAdmin && <option value="google">Google</option>}
              {isAdmin && <option value="venice">Venice</option>}
              <option value="custom">Custom Endpoint</option>
            </select>
            {!isAdmin && (
              <p className="text-xs text-gray-500 mt-1">Only admins can change provider</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={model.description || ''}
              onChange={(e) => onChange('description', e.target.value)}
              placeholder="Latest GPT-4 model with improved performance and longer context..."
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>

          {/* Checkboxes */}
          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={model.supports_vision || false}
                onChange={(e) => onChange('supports_vision', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 flex items-center">
                Vision
                <InfoIcon field="vision" position="above" />
              </span>
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={model.supports_streaming || false}
                onChange={(e) => onChange('supports_streaming', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 flex items-center">
                Streaming
                <InfoIcon field="streaming" position="above" />
              </span>
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={model.isPrivacyCompliant || false}
                onChange={(e) => onChange('isPrivacyCompliant', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 flex items-center">
                Privacy
                <InfoIcon field="privacy" position="above" />
              </span>
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={model.isUncensored || false}
                onChange={(e) => onChange('isUncensored', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 flex items-center">
                Uncensored
                <InfoIcon field="uncensored" position="above" />
              </span>
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={model.taskDeterminationModel || false}
                onChange={(e) => onChange('taskDeterminationModel', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 flex items-center">
                Task Det
                <InfoIcon field="taskDet" position="above" />
              </span>
            </label>

            {isAdmin && (
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={model.requires_premium || false}
                  onChange={(e) => onChange('requires_premium', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 flex items-center">
                  Premium
                  <InfoIcon field="premium" position="above" />
                </span>
              </label>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CapabilitiesTab({ capabilities, onChange }: any) {
  const capabilityConfig = [
    { key: 'reasoning', label: 'Reasoning', icon: '🧠', description: 'Logical analysis and problem-solving' },
    { key: 'coding', label: 'Coding', icon: '💻', description: 'Programming and code generation' },
    { key: 'creativity', label: 'Creativity', icon: '🎨', description: 'Creative writing and ideation' },
    { key: 'conversational', label: 'Conversational', icon: '💬', description: 'Natural dialogue and chat' },
    { key: 'speed', label: 'Speed', icon: '⚡', description: 'Response time and efficiency' },
    { key: 'webSearch', label: 'Web Search', icon: '🔍', description: 'Internet search capabilities' },
    { key: 'enhancedReasoning', label: 'Enhanced Reasoning', icon: '🤔', description: 'Deep analytical thinking' },
    { key: 'imageGeneration', label: 'Image Generation', icon: '🖼️', description: 'Creating images from text' }
  ];

  const leftColumn = capabilityConfig.slice(0, 4);
  const rightColumn = capabilityConfig.slice(4);

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">Capability Scores</h3>
        <p className="text-sm text-gray-500 mb-3">
          Rate each capability from 0 (not capable) to 5 (excellent). This helps us route requests to the best model.
        </p>
      </div>

      <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-800">
          💡 <strong>Tip:</strong> Set capabilities that match the model's strengths. Lower scores are okay - they help us choose the right model for each task.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-3">
          {leftColumn.map(({ key, label, icon, description }) => (
            <CapabilitySlider
              key={key}
              label={label}
              icon={icon}
              description={description}
              value={capabilities[key] || 0}
              onChange={(value) => onChange(key, value)}
            />
          ))}
        </div>

        {/* Right Column */}
        <div className="space-y-3">
          {rightColumn.map(({ key, label, icon, description }) => (
            <CapabilitySlider
              key={key}
              label={label}
              icon={icon}
              description={description}
              value={capabilities[key] || 0}
              onChange={(value) => onChange(key, value)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface CapabilitySliderProps {
  label: string;
  icon: string;
  description: string;
  value: number;
  onChange: (value: number) => void;
}

function CapabilitySlider({ label, icon, description, value, onChange }: CapabilitySliderProps) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{icon}</span>
          <div>
            <div className="font-medium text-gray-900">{label}</div>
            <div className="text-xs text-gray-500">{description}</div>
          </div>
        </div>
        <div className="text-lg font-bold text-blue-600">{value}</div>
      </div>

      <div className="relative">
        <input
          type="range"
          min="0"
          max="5"
          step="1"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
          style={{
            background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(value / 5) * 100}%, #e5e7eb ${(value / 5) * 100}%, #e5e7eb 100%)`
          }}
        />
        <div className="flex justify-between mt-0.5">
          <span className="text-xs text-gray-500">Poor</span>
          <span className="text-xs text-gray-500">Excellent</span>
        </div>
      </div>
    </div>
  );
}

function EndpointTab({ model, onChange, onTestConnection, testingConnection, connectionStatus, error }: any) {
  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Custom Endpoint Configuration</h3>
        <p className="text-sm text-gray-500 mb-3">
          Configure your OpenAI-compatible API endpoint. Your API key will be encrypted for security.
        </p>
      </div>

      <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          🔒 Your endpoint must be OpenAI-compatible (same request/response format)
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Endpoint URL *
        </label>
        <input
          type="url"
          value={model.customEndpointUrl || ''}
          onChange={(e) => onChange('customEndpointUrl', e.target.value)}
          placeholder="https://api.example.com/v1/chat/completions"
          autoComplete="new-password"
          name="endpoint-url"
          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="text-xs text-gray-500 mt-1">Full URL including the path</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          API Key *
        </label>
        <input
          type="password"
          value={model.customApiKey || ''}
          onChange={(e) => onChange('customApiKey', e.target.value)}
          placeholder="sk-..."
          autoComplete="new-password"
          name="api-key"
          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="text-xs text-gray-500 mt-1">
          {model.customApiKeyEncrypted
            ? 'Leave blank to keep existing key. Enter new key to replace.'
            : 'Will be encrypted when saved'}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Custom Headers (JSON)
        </label>
        <textarea
          value={typeof model.customHeaders === 'string'
            ? model.customHeaders
            : JSON.stringify(model.customHeaders || {}, null, 2)}
          onChange={(e) => onChange('customHeaders', e.target.value)}
          placeholder='{\n  "X-Custom-Header": "value"\n}'
          className="w-full p-2 border border-gray-300 rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={4}
        />
        <p className="text-xs text-gray-500 mt-1">Optional: Additional headers to include in API requests</p>
      </div>

      <div className="pt-1">
        <button
          onClick={onTestConnection}
          disabled={testingConnection || !model.customEndpointUrl || !model.customApiKey}
          className={`px-4 py-2 rounded-md font-medium ${
            testingConnection
              ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
              : connectionStatus === 'success'
              ? 'bg-green-600 hover:bg-green-700 text-white'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          {testingConnection ? 'Testing...' : connectionStatus === 'success' ? '✓ Connection Successful' : 'Test Connection'}
        </button>

        {connectionStatus === 'error' && error && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 text-red-700 rounded text-sm">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
